import React, { useState, useEffect, useCallback, useRef } from "react";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Rating,
  Avatar,
  Tooltip,
  Modal,
  Paper,
  Container,
  Alert,
  Snackbar,
  Pagination,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  LinearProgress,
  Tabs,
  Tab,
  Skeleton
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Upload as UploadIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from "@mui/icons-material";
import Notes from "./header";
import FooterManual from "./footerManual";

// Styled Card component matching the second design
const StyledCard = ({ children, isHovered, onMouseEnter, onMouseLeave, ...props }) => (
  <Card
    {...props}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
      boxShadow: isHovered ? "0 8px 24px rgba(0,0,0,0.15)" : "0 2px 8px rgba(0,0,0,0.08)",
      transform: isHovered ? "translateY(-4px)" : "translateY(0)",
      "&:hover": {
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        transform: "translateY(-4px)"
      },
      cursor: "pointer",
      ...props.sx
    }}
  >
    {children}
  </Card>
);

// Media Skeleton Loader Component
const MediaSkeleton = ({ count = 3 }) => (
  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
    {Array.from({ length: count }).map((_, idx) => (
      <Skeleton
        key={idx}
        variant="rounded"
        width={70}
        height={70}
        sx={{ borderRadius: "6px" }}
      />
    ))}
  </Box>
);

const UserReviews = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [reviews, setReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filterRating, setFilterRating] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalCounts, setTotalCounts] = useState({
    totalReviews: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0
  });
  const [hoveredCard, setHoveredCard] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // New states for edit functionality
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editReviewData, setEditReviewData] = useState({
    rating: 5,
    reviewTitle: "",
    shareYourExperince: ""
  });
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaToDelete, setMediaToDelete] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [editTab, setEditTab] = useState(0);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [mediaLoading, setMediaLoading] = useState({});

  // New state for delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mediaToDeleteIndex, setMediaToDeleteIndex] = useState(null);

  // Refs for caching and tracking
  const reviewsDataCache = useRef(null);
  const isInitialLoad = useRef(true);

  // Get token dynamically
  const getAuthToken = useCallback(() => {
    const tokenFromStorage = localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    if (!tokenFromStorage) {
      throw new Error("No authentication token found. Please log in.");
    }

    return tokenFromStorage.startsWith("Bearer ")
      ? tokenFromStorage
      : `Bearer ${tokenFromStorage}`;
  }, []);

  const getProfileImageWithAuth = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/user/get-image/`,
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            "Authorization": token,
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        return imageUrl;
      }

      if (response.status === 401) {
        console.error("Unauthorized: Token may be expired or invalid");
      }

      console.error("Failed to fetch profile image:", response.status);
      return null;
    } catch (error) {
      console.error("Error fetching profile image:", error);
      return null;
    }
  }, [getAuthToken]);

  const API_BASE_URL = "https://car.amar.co.jp/api/review-rating/review-rating/get/user-reviews";

  // Function to fetch car image using carModelId (numeric ID)
  const fetchCarImage = async (carModelId) => {
    if (!carModelId) return null;

    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`,
        {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Accept-Language': 'en'
          }
        }
      );

      if (response.ok && response.headers.get('content-type')?.includes('image')) {
        const imageBlob = await response.blob();
        return URL.createObjectURL(imageBlob);
      }
    } catch (error) {
      console.error(`Error fetching car image for ID ${carModelId}:`, error);
    }

    return null;
  };

  // Function to fetch media for a specific review with loading state
  const fetchReviewMedia = async (ratingId) => {
    try {
      // Set loading state for this review's media
      setMediaLoading(prev => ({ ...prev, [ratingId]: true }));
      
      const token = getAuthToken();
      const response = await fetch(
        `https://car.amar.co.jp/api/review-rating/review-rating/media/${ratingId}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) {
        setMediaLoading(prev => ({ ...prev, [ratingId]: false }));
        return [];
      }

      const result = await response.json();

      if (result.success && result.data && result.data.allMedia) {
        const mediaFiles = [];

        for (const media of result.data.allMedia) {
          let mediaUrl = null;

          if (media.filePath) {
            mediaUrl = `https://car.amar.co.jp${media.filePath}`;
          } else if (media.base64Content) {
            try {
              const base64Data = media.base64Content;
              const byteCharacters = atob(base64Data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const type = media.mediaType === 'VIDEO' ? 'video/mp4' : 'image/jpeg';
              const blob = new Blob([byteArray], { type });
              mediaUrl = URL.createObjectURL(blob);
            } catch (error) {
              console.error('Error processing base64 content:', error);
            }
          }

          if (mediaUrl) {
            mediaFiles.push({
              mediaId: media.mediaId,
              type: media.mediaType === "VIDEO" ? "video" : "image",
              url: mediaUrl,
              thumbnail: mediaUrl,
              fileName: media.fileName || `media-${media.mediaId}`,
              fileSize: formatFileSize(media.fileSize),
              mediaType: media.mediaType,
              displayOrder: media.displayOrder
            });
          }
        }

        // Clear loading state after media is loaded
        setTimeout(() => {
          setMediaLoading(prev => ({ ...prev, [ratingId]: false }));
        }, 300); // Small delay for better UX

        return mediaFiles;
      }
    } catch (error) {
      console.error(`Error fetching media for review ${ratingId}:`, error);
      setMediaLoading(prev => ({ ...prev, [ratingId]: false }));
    }

    setMediaLoading(prev => ({ ...prev, [ratingId]: false }));
    return [];
  };

  // Format date from API array format
  const formatDateFromArray = (dateArray) => {
    if (!dateArray || dateArray.length < 3) return new Date().toISOString().split('T')[0];

    const [year, month, day] = dateArray;
    const date = new Date(year, month - 1, day);
    return date.toISOString().split('T')[0];
  };

  // Map API status to UI status
  const mapApiStatusToUI = (apiStatus) => {
    const statusMap = {
      "PENDING": "pending",
      "APPROVED": "Approved",
      "REJECTED": "rejected"
    };
    return statusMap[apiStatus] || apiStatus?.toLowerCase() || "pending";
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Function to load basic review data first (without media)
  const loadBasicReviewData = async (reviewData) => {
    let carModelId = null;

    if (reviewData.carModelId) {
      carModelId = reviewData.carModelId;
    } else if (reviewData.carModelName && typeof reviewData.carModelName === 'number') {
      carModelId = reviewData.carModelName;
    } else if (reviewData.carModelName && typeof reviewData.carModelName === 'object' && reviewData.carModelName.id) {
      carModelId = reviewData.carModelName.id;
    } else if (reviewData.carModel) {
      carModelId = reviewData.carModel;
    }

    // Start car image fetch but don't wait for it
    const carImagePromise = carModelId ? fetchCarImage(carModelId) : Promise.resolve(null);

    return {
      id: reviewData.ratingId,
      reviewId: `REV-${reviewData.ratingId.toString().padStart(3, '0')}`,
      userId: reviewData.userId || reviewData.email,
      userName: `${reviewData.firstName || ''} ${reviewData.lastName || ''}`.trim() || 'Anonymous User',
      userCountry: reviewData.userCountry || "",
      userAvatar: profileImageUrl,
      carModelName: reviewData.carModelNameName ||
        (typeof reviewData.carModelName === 'object' ? reviewData.carModelName.name : reviewData.carModelName) ||
        "Unknown Car Model",
      carBrandName: reviewData.carBrandName || "Car Brand",
      carModelId: carModelId,
      carImage: null, // Will be populated later
      carImagePromise: carImagePromise, // Store the promise
      rating: reviewData.rating || 0,
      title: reviewData.reviewTitle || "No Title",
      comment: reviewData.shareYourExperince || "No comment provided",
      date: formatDateFromArray(reviewData.createdAt),
      status: mapApiStatusToUI(reviewData.reviewStatus),
      verified: reviewData.verifiedPurchase || false,
      media: [], // Start with empty media
      mediaLoading: true, // Flag to show media is loading
      apiData: reviewData,
      orderId: reviewData.orderId
    };
  };

  // Function to load media for a review in the background
  const loadReviewMediaInBackground = async (reviewId, carImagePromise) => {
    try {
      // Load media and car image in parallel
      const [media, carImage] = await Promise.all([
        fetchReviewMedia(reviewId),
        carImagePromise
      ]);

      // Update the review with loaded media and car image
      setReviews(prevReviews => 
        prevReviews.map(review => 
          review.id === reviewId 
            ? { 
                ...review, 
                media: media,
                carImage: carImage,
                mediaLoading: false
              }
            : review
        )
      );

    } catch (error) {
      console.error(`Error loading media for review ${reviewId}:`, error);
      
      // Even if media fails, try to at least set the car image
      try {
        const carImage = await carImagePromise;
        setReviews(prevReviews => 
          prevReviews.map(review => 
            review.id === reviewId 
              ? { 
                  ...review, 
                  media: [],
                  carImage: carImage,
                  mediaLoading: false
                }
              : review
          )
        );
      } catch (carImageError) {
        console.error(`Error loading car image for review ${reviewId}:`, carImageError);
        setReviews(prevReviews => 
          prevReviews.map(review => 
            review.id === reviewId 
              ? { 
                  ...review, 
                  media: [],
                  carImage: null,
                  mediaLoading: false
                }
              : review
          )
        );
      }
    }
  };

  // Main function to fetch reviews with progressive loading
  const fetchReviews = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }

    try {
      const token = getAuthToken();
      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data && Array.isArray(result.data.reviews)) {
        // Cache the raw data
        reviewsDataCache.current = result.data;

        // Fetch profile image
        const userAvatarUrl = await getProfileImageWithAuth();
        setProfileImageUrl(userAvatarUrl);

        // First, create basic review objects without media
        const basicReviews = await Promise.all(
          result.data.reviews.map(async (reviewData) => {
            const basicReview = await loadBasicReviewData(reviewData);
            return basicReview;
          })
        );

        // Set the basic reviews immediately (shows content fast)
        setReviews(basicReviews);

        // Update counts
        const pendingCount = result.data.reviews.filter(r => r.reviewStatus === "PENDING").length;
        const approvedCount = result.data.reviews.filter(r => r.reviewStatus === "APPROVED").length;
        const rejectedCount = result.data.reviews.filter(r => r.reviewStatus === "REJECTED").length;

        setTotalCounts({
          totalReviews: result.data.totalReviews || result.data.reviews.length,
          pendingCount: result.data.pendingCount || pendingCount,
          approvedCount: result.data.approvedCount || approvedCount,
          rejectedCount: result.data.rejectedCount || rejectedCount
        });

        // Then load media and car images in the background for each review
        basicReviews.forEach(review => {
          loadReviewMediaInBackground(review.id, review.carImagePromise);
        });

      } else {
        setReviews([]);
        setTotalCounts({
          totalReviews: 0,
          pendingCount: 0,
          approvedCount: 0,
          rejectedCount: 0
        });
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
      setTotalCounts({
        totalReviews: 0,
        pendingCount: 0,
        approvedCount: 0,
        rejectedCount: 0
      });
      
      setSnackbar({
        open: true,
        message: "Failed to load reviews. Please try again.",
        severity: "error"
      });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      isInitialLoad.current = false;
    }
  };

  // Optimized refresh function
  const refreshData = async () => {
    setIsProcessing(true);
    
    try {
      await fetchReviews(false); // Don't show loading overlay
      
      setSnackbar({
        open: true,
        message: "Data refreshed successfully!",
        severity: "success"
      });
    } catch (error) {
      console.error("Error refreshing data:", error);
      setSnackbar({
        open: true,
        message: "Failed to refresh data",
        severity: "error"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // EDIT FUNCTIONS
  const handleEditClick = (review) => {
    setEditingReviewId(review.id);
    setEditReviewData({
      rating: review.rating,
      reviewTitle: review.title,
      shareYourExperince: review.comment
    });
    setMediaFiles(review.media || []);
    setMediaToDelete([]);
    setEditTab(0);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingReviewId(null);
    setEditReviewData({
      rating: 5,
      reviewTitle: "",
      shareYourExperince: ""
    });
    setMediaFiles([]);
    setMediaToDelete([]);
    setIsProcessing(false);
  };

  const handleEditSubmit = async () => {
    setIsProcessing(true);

    try {
      const token = getAuthToken();

      // First, delete any marked media
      if (mediaToDelete.length > 0) {
        for (const mediaId of mediaToDelete) {
          try {
            const deleteResponse = await fetch(
              `https://car.amar.co.jp/api/review-rating/review-rating/delete-media/${mediaId}`,
              {
                method: 'DELETE',
                headers: {
                  'accept': 'application/json',
                  'Authorization': token
                }
              }
            );

            if (!deleteResponse.ok) {
              console.warn(`Failed to delete media ${mediaId}`);
            }
          } catch (error) {
            console.error(`Error deleting media ${mediaId}:`, error);
          }
        }
      }

      // Update review text and rating
      const updateResponse = await fetch(
        `https://car.amar.co.jp/api/review-rating/review-rating/update/${editingReviewId}`,
        {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            rating: editReviewData.rating,
            reviewTitle: editReviewData.reviewTitle,
            shareYourExperince: editReviewData.shareYourExperince
          })
        }
      );

      if (!updateResponse.ok) {
        throw new Error('Failed to update review');
      }

      const updateResult = await updateResponse.json();

      // Upload new media files
      const newMediaFiles = mediaFiles.filter(file => file.isNew);
      if (newMediaFiles.length > 0) {
        for (const file of newMediaFiles) {
          const formData = new FormData();
          formData.append('mediaFiles', file.file);

          try {
            const uploadResponse = await fetch(
              `https://car.amar.co.jp/api/review-rating/review-rating/update/add-media/${editingReviewId}`,
              {
                method: 'POST',
                headers: {
                  'accept': 'application/json',
                  'Authorization': token
                },
                body: formData
              }
            );

            if (!uploadResponse.ok) {
              console.warn('Failed to upload media:', file.name);
            }
          } catch (uploadError) {
            console.error('Error uploading media:', uploadError);
          }
        }
      }

      setSnackbar({
        open: true,
        message: updateResult.message || "Review updated successfully!",
        severity: "success"
      });

      // Refresh only the updated review
      await refreshData();
      handleEditDialogClose();

    } catch (error) {
      console.error('Error updating review:', error);
      setSnackbar({
        open: true,
        message: "You cannot edit a rejected or Pending review. Please wait for Admin Approval or submit a new review instead",
        severity: "error"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMediaUpload = (event) => {
    const files = Array.from(event.target.files);
    const newMediaFiles = files.map(file => ({
      isNew: true,
      file: file,
      type: file.type.startsWith('video') ? 'video' : 'image',
      fileName: file.name,
      fileSize: formatFileSize(file.size),
      url: URL.createObjectURL(file)
    }));

    setMediaFiles(prev => [...prev, ...newMediaFiles]);
  };

  // Open delete confirmation dialog
  const handleOpenDeleteConfirm = (index) => {
    setMediaToDeleteIndex(index);
    setDeleteConfirmOpen(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setMediaToDeleteIndex(null);
  };

  // Confirm and execute media deletion
  const handleConfirmDeleteMedia = () => {
    if (mediaToDeleteIndex !== null) {
      const media = mediaFiles[mediaToDeleteIndex];
      if (media.mediaId && !media.isNew) {
        setMediaToDelete(prev => [...prev, media.mediaId]);
      }
      setMediaFiles(prev => prev.filter((_, i) => i !== mediaToDeleteIndex));
      handleCloseDeleteConfirm();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      Approved: "#e8f5e9",
      pending: "#fff3e0",
      rejected: "#f5f5f5",
    };
    return colors[status] || "#f5f5f5";
  };

  const getStatusTextColor = (status) => {
    const colors = {
      Approved: "#2e7d32",
      pending: "#e65100",
      rejected: "#e60000ff",
    };
    return colors[status] || "#333";
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "#4caf50";
    if (rating === 3) return "#ff9800";
    return "#f44336";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      return dateString;
    }
  };

  const handleOpenDetails = (review) => {
    setSelectedReview(review);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedReview(null);
  };

  const handleOpenMedia = (review, index) => {
    if (review && review.media && review.media[index]) {
      setSelectedReview(review);
      setSelectedMedia(review.media[index]);
      setCurrentMediaIndex(index);
    }
  };

  const handleCloseMedia = () => {
    setSelectedMedia(null);
    setCurrentMediaIndex(0);
  };

  const handleNextMedia = () => {
    if (selectedReview && selectedReview.media && selectedReview.media.length > 0) {
      const nextIndex = (currentMediaIndex + 1) % selectedReview.media.length;
      setCurrentMediaIndex(nextIndex);
      setSelectedMedia(selectedReview.media[nextIndex]);
    }
  };

  const handlePreviousMedia = () => {
    if (selectedReview && selectedReview.media && selectedReview.media.length > 0) {
      const prevIndex = currentMediaIndex === 0
        ? selectedReview.media.length - 1
        : currentMediaIndex - 1;
      setCurrentMediaIndex(prevIndex);
      setSelectedMedia(selectedReview.media[prevIndex]);
    }
  };

  // Filtering and pagination
  const filteredReviews = reviews.filter((review) => {
    const matchRating = filterRating === "all" || review.rating.toString() === filterRating;
    const matchStatus = filterStatus === "all" || review.status === filterStatus;
    const matchSearch =
      searchText === "" ||
      review.reviewId?.toLowerCase().includes(searchText.toLowerCase()) ||
      review.userName?.toLowerCase().includes(searchText.toLowerCase()) ||
      review.carModelName?.toLowerCase().includes(searchText.toLowerCase()) ||
      review.carBrandName?.toLowerCase().includes(searchText.toLowerCase()) ||
      review.title?.toLowerCase().includes(searchText.toLowerCase());

    return matchRating && matchStatus && matchSearch;
  });

  const totalPages = Math.ceil(filteredReviews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedReviews = filteredReviews.slice(startIndex, startIndex + itemsPerPage);

  // Image Placeholder Component
  const ImagePlaceholder = ({ type, text, size = "100%" }) => {
    const isUser = type === 'user';
    const isCar = type === 'car';

    return (
      <Box
        sx={{
          width: size,
          height: size,
          backgroundColor: isUser ? "#d32f2f" : isCar ? "#2196f3" : "#666",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: isUser ? "16px" : isCar ? "24px" : "12px",
          fontWeight: "bold",
          borderRadius: isUser ? "50%" : "8px",
          overflow: "hidden"
        }}
      >
        {text || (isUser ? "U" : isCar ? "No Image" : "No Image")}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", backgroundColor: "#f5f5f5", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: "12px" }}>
          <Notes />

          {/* Header */}
          <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ color: "#d32f2f", fontWeight: "bold", mb: 1 }}>
                User Reviews Management
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Total Reviews: {totalCounts.totalReviews} |
                Approved: {totalCounts.approvedCount} |
                Pending: {totalCounts.pendingCount} |
                Rejected: {totalCounts.rejectedCount}
                <br />
                Displaying: {paginatedReviews.length} of {filteredReviews.length}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={refreshData}
                disabled={isProcessing || loading}
                sx={{
                  backgroundColor: "#d32f2f",
                  color: "white",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "#b71c1c"
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0"
                  }
                }}
              >
                {isProcessing ? "Refreshing..." : "Refresh"}
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 4, p: 3, backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search reviews"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Rating</InputLabel>
                <Select
                  value={filterRating}
                  label="Rating"
                  onChange={(e) => {
                    setFilterRating(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value="all">All Ratings</MenuItem>
                  <MenuItem value="5">⭐⭐⭐⭐⭐ (5 stars)</MenuItem>
                  <MenuItem value="4">⭐⭐⭐⭐ (4 stars)</MenuItem>
                  <MenuItem value="3">⭐⭐⭐ (3 stars)</MenuItem>
                  <MenuItem value="2">⭐⭐ (2 stars)</MenuItem>
                  <MenuItem value="1">⭐ (1 star)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Initial Loading State */}
          {loading && isInitialLoad.current ? (
            <Box sx={{
              textAlign: "center",
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Box
                component="img"
                src="/image/footer-logo-small.svg"
                alt="Loading..."
                sx={{
                  width: 120,
                  height: 'auto',
                  mb: 2,
                  opacity: 0.8,
                  animation: "pulse 1.5s ease-in-out infinite"
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Loading reviews...
              </Typography>
            </Box>
          ) : null}

          {/* Grid Layout - Shows immediately with skeleton loading for media */}
          {(!loading || !isInitialLoad.current) && filteredReviews.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No reviews found
              </Typography>
              <Typography color="text.disabled">
                {reviews.length === 0 ? "" : "Try adjusting your filters or search criteria"}
              </Typography>
            </Box>
          ) : (!loading || !isInitialLoad.current) ? (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {paginatedReviews.map((review) => (
                  <Grid item xs={12} sm={6} lg={4} key={review.id}>
                    <StyledCard
                      isHovered={hoveredCard === review.id}
                      onMouseEnter={() => setHoveredCard(review.id)}
                      onMouseLeave={() => setHoveredCard(null)}
                      onClick={() => handleOpenDetails(review)}
                    >
                      {/* Image Section with Skeleton */}
                      <Box sx={{ position: "relative", height: 200, overflow: "hidden" }}>
                        {review.carImage ? (
                          <CardMedia
                            component="img"
                            height="200"
                            image={review.carImage}
                            alt={review.carModelName}
                            sx={{
                              transition: "transform 0.5s ease",
                              transform: hoveredCard === review.id ? "scale(1.05)" : "scale(1)",
                              objectFit: "cover"
                            }}
                          />
                        ) : (
                          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5" }}>
                            <Skeleton
                              variant="rounded"
                              width="100%"
                              height="100%"
                              sx={{ bgcolor: '#e0e0e0' }}
                            />
                          </Box>
                        )}

                        {/* Status Badge */}  
                        <Chip
                          label={review.status}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            backgroundColor: getStatusColor(review.status),
                            color: getStatusTextColor(review.status),
                            fontWeight: 600,
                            textTransform: "capitalize",
                            zIndex: 2
                          }}
                        />
                      </Box>

                      {/* Card Content */}
                      <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        {/* Car Info */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "1.125rem",
                              fontWeight: 700,
                              mb: 0.5,
                              color: "text.primary",
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {review.carBrandName} {review.carModelName}
                          </Typography>
                        </Box>

                        {/* Rating */}
                        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                          <Rating
                            value={review.rating}
                            readOnly
                            size="small"
                            icon={<StarIcon fontSize="inherit" />}
                            emptyIcon={<StarBorderIcon fontSize="inherit" />}
                            sx={{
                              "& .MuiRating-iconFilled": {
                                color: getRatingColor(review.rating),
                              },
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: getRatingColor(review.rating),
                            }}
                          >
                            {review.rating}.0
                          </Typography>
                          {review.verified && (
                            <Tooltip title="Verified Purchase">
                              <VerifiedIcon sx={{ color: "#0c7de7ff", fontSize: "1rem" }} />
                            </Tooltip>
                          )}
                        </Box>

                        {/* Review Title */}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {review.title}
                        </Typography>

                        {/* Review Comment */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {review.comment}
                        </Typography>

                        {/* Media Gallery with Loading State */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              display: "block",
                              mb: 1
                            }}
                          >
                            Media ({review.mediaLoading ? "Loading..." : review.media.length})
                          </Typography>
                          
                          {review.mediaLoading || mediaLoading[review.id] ? (
                            <MediaSkeleton count={3} />
                          ) : review.media && review.media.length > 0 ? (
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              {review.media.slice(0, 3).map((media, idx) => (
                                <Box
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenMedia(review, idx);
                                  }}
                                  sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: "6px",
                                    overflow: "hidden",
                                    border: "2px solid",
                                    borderColor: "divider",
                                    cursor: "pointer",
                                    position: "relative",
                                    backgroundColor: "#f5f5f5"
                                  }}
                                >
                                  {media.type === "video" ? (
                                    <Box sx={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      backgroundColor: "#333"
                                    }}>
                                      <PlayArrowIcon sx={{ color: "white", fontSize: "2rem" }} />
                                    </Box>
                                  ) : (
                                    <CardMedia
                                      component="img"
                                      height="70"
                                      image={media.thumbnail || media.url}
                                      alt={`Review media ${idx + 1}`}
                                      sx={{ objectFit: "cover" }}
                                    />
                                  )}
                                </Box>
                              ))}
                              {review.media.length > 3 && (
                                <Box
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenMedia(review, 0);
                                  }}
                                  sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: "6px",
                                    backgroundColor: "#f5f5f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "2px solid",
                                    borderColor: "divider",
                                    cursor: "pointer"
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                                    +{review.media.length - 3}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ mb: 2, p: 1, backgroundColor: "#e2c6c6ff", borderRadius: "6px", textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary">
                                No media uploaded
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* User Info */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                          <Box sx={{ position: "relative" }}>
                            {review.userAvatar ? (
                              <Avatar
                                src={review.userAvatar}
                                alt={review.userName}
                                sx={{ width: 40, height: 40 }}
                              />
                            ) : (
                              <Skeleton
                                variant="circular"
                                width={40}
                                height={40}
                                sx={{ bgcolor: '#e0e0e0' }}
                              />
                            )}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="text.primary">
                              {review.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(review.date)}
                              {review.userCountry && ` • ${review.userCountry}`}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(review);
                              }}
                              sx={{
                                backgroundColor: "grey",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "Darkgrey"
                                }
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDetails(review);
                              }}
                              sx={{
                                backgroundColor: "#d32f2f",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#b71c1c"
                                }
                              }}
                            >
                              View
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Items per page:</Typography>
                 
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, value) => setCurrentPage(value)}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>
                 <Select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value={6}>6</MenuItem>
                    <MenuItem value={9}>9</MenuItem>
                    <MenuItem value={12}>12</MenuItem>
                  </Select>
              </Box>
            </>
          ) : null}
        </Paper>
      </Container>

      {/* Edit Review Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6">Edit Review</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Review ID: REV-{editingReviewId?.toString().padStart(3, '0')}
          </Typography>
        </DialogTitle>

        <Tabs value={editTab} onChange={(e, newValue) => setEditTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Review Details" />
          <Tab label="Media Management" />
        </Tabs>

        <DialogContent>
          {editTab === 0 ? (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Rating
                  </Typography>
                  <Rating
                    value={editReviewData.rating}
                    onChange={(event, newValue) => {
                      setEditReviewData(prev => ({
                        ...prev,
                        rating: newValue || 5
                      }));
                    }}
                    size="large"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Review Title"
                    value={editReviewData.reviewTitle}
                    onChange={(e) => setEditReviewData(prev => ({
                      ...prev,
                      reviewTitle: e.target.value
                    }))}
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Your Experience"
                    value={editReviewData.shareYourExperince}
                    onChange={(e) => setEditReviewData(prev => ({
                      ...prev,
                      shareYourExperince: e.target.value
                    }))}
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Media
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,video/*"
                    onChange={handleMediaUpload}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Maximum 10 files, Images & Videos allowed
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {mediaFiles.map((media, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Paper
                      sx={{
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: media.isNew ? 'primary.main' : 'divider'
                      }}
                    >
                      {media.type === "video" ? (
                        <Box sx={{
                          aspectRatio: "1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#333"
                        }}>
                          <PlayArrowIcon sx={{ color: "white", fontSize: "2rem" }} />
                        </Box>
                      ) : (
                        <CardMedia
                          component="img"
                          image={media.url}
                          alt={media.fileName}
                          sx={{ aspectRatio: "1", objectFit: "cover" }}
                        />
                      )}
                      <Box sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '50%'
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteConfirm(index)}
                          sx={{ color: 'white' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: "rgba(0,0,0,0.75)",
                        color: "white",
                        p: 0.5
                      }}>
                        <Typography variant="caption" noWrap fontSize="0.6rem">
                          {media.fileName}
                        </Typography>
                      </Box>
                      {media.isNew && (
                        <Box sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          backgroundColor: 'primary.main',
                          color: 'white',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: '4px',
                          fontSize: '0.6rem'
                        }}>
                          NEW
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={handleEditDialogClose}
            startIcon={<CancelIcon />}
            color="inherit"
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            startIcon={
              isProcessing ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            variant="contained"
            disabled={isProcessing}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              "&:hover": {
                backgroundColor: "#b71c1c"
              },
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                color: "#999"
              }
            }}
          >
            {isProcessing ? 'Saving Changes...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "#fff5f5", borderBottom: "1px solid #ffcdd2" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6" color="error">
              Delete Media
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this media file?
          </Typography>
          {mediaToDeleteIndex !== null && mediaFiles[mediaToDeleteIndex] && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#f9f9f9", borderRadius: "6px" }}>
              <Typography variant="body2" color="text.secondary">
                File: <strong>{mediaFiles[mediaToDeleteIndex].fileName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: <strong>{mediaFiles[mediaToDeleteIndex].type.toUpperCase()}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Size: <strong>{mediaFiles[mediaToDeleteIndex].fileSize}</strong>
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Note: This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={handleCloseDeleteConfirm}
            color="inherit"
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteMedia}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isProcessing}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Modal */}
      <>
        {/* Review Details Modal */}
        <Modal
          open={showDetails}
          onClose={handleCloseDetails}
          aria-labelledby="review-details-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: 1200,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            {selectedReview && (
              <>
                {/* Left Side - Car Image (Full Height) */}
                <Box sx={{
                  width: { xs: '100%', md: '35%' },
                  minHeight: { xs: 200, md: 'auto' },
                  maxHeight: { xs: 300, md: 'none' },
                  position: 'relative',
                  backgroundColor: '#f5f5f5',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Car Image - Takes full available space */}
                  <Box sx={{
                    flex: 1,
                    position: 'relative',
                    minHeight: { xs: 200, md: 400 }
                  }}>
                    {selectedReview.carImage ? (
                      <CardMedia
                        component="img"
                        image={selectedReview.carImage}
                        alt={selectedReview.carModelName}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                      />
                    ) : (
                      <Box sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#f5f5f5"
                      }}>
                        <Skeleton
                          variant="rounded"
                          width="100%"
                          height="100%"
                          sx={{ bgcolor: '#e0e0e0' }}
                        />
                      </Box>
                    )}

                    {/* Car Info Overlay */}
                    <Box sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 40%, transparent 100%)",
                      p: 2.5
                    }}>
                      <Typography variant="h6" sx={{ color: "white", fontWeight: 700, mb: 0.5, fontSize: '1.1rem' }}>
                        {selectedReview.carBrandName} {selectedReview.carModelName}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Additional Car Details Section */}
                  <Box sx={{
                    p: 2.5,
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    borderTop: '1px solid rgba(0,0,0,0.08)'
                  }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontSize="0.7rem">
                        VEHICLE INFORMATION
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            Brand:
                          </Typography>
                          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
                            {selectedReview.carBrandName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            Model:
                          </Typography>
                          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
                            {selectedReview.carModelName}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Quick Stats */}
                    <Box sx={{
                      display: 'flex',
                      gap: 1.5,
                      pt: 1.5,
                      borderTop: '1px solid rgba(0,0,0,0.08)'
                    }}>
                      <Box sx={{
                        flex: 1,
                        textAlign: 'center',
                        p: 1,
                        backgroundColor: 'white',
                        borderRadius: '6px'
                      }}>
                        <Typography variant="h6" fontWeight={700} color={getRatingColor(selectedReview.rating)}>
                          {selectedReview.rating}.0
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                          RATING
                        </Typography>
                      </Box>
                      <Box sx={{
                        flex: 1,
                        textAlign: 'center',
                        p: 1,
                        backgroundColor: 'white',
                        borderRadius: '6px'
                      }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main">
                          {selectedReview.media?.length || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                          MEDIA
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Right Side - Content */}
                <Box sx={{
                  width: { xs: '100%', md: '65%' },
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative'
                }}>
                  {/* Close Button */}
                  <IconButton
                    onClick={handleCloseDetails}
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      backgroundColor: "rgba(255,255,255,0.95)",
                      zIndex: 10,
                      boxShadow: 2,
                      "&:hover": {
                        backgroundColor: "white"
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  {/* Scrollable Content Area */}
                  <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 3,
                    pt: 4
                  }}>
                    {/* Rating and Status */}
                    <Box sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                      p: 2.5,
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      flexWrap: 'wrap',
                      gap: 2
                    }}>
                      <Box>
                        <Rating
                          value={selectedReview.rating}
                          readOnly
                          size="large"
                          icon={<StarIcon fontSize="inherit" />}
                          emptyIcon={<StarBorderIcon fontSize="inherit" />}
                          sx={{
                            "& .MuiRating-iconFilled": {
                              color: getRatingColor(selectedReview.rating),
                            },
                            mb: 1
                          }}
                        />
                        <Typography variant="h5" fontWeight={700} color={getRatingColor(selectedReview.rating)}>
                          {selectedReview.rating}.0 / 5.0
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        {selectedReview.verified && (
                          <Chip
                            label="Verified Purchase"
                            sx={{
                              backgroundColor: "#e8f1f5ff",
                              color: "#007bffff",
                              mb: 1
                            }}
                          />
                        )}
                        <Chip
                          label={selectedReview.status}
                          sx={{
                            backgroundColor: getStatusColor(selectedReview.status),
                            color: getStatusTextColor(selectedReview.status),
                            fontWeight: 600,
                            textTransform: "capitalize"
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Review Title Section - Inside Border */}
                    <Box sx={{
                      mb: 3,
                      border: '2px solid',
                      borderColor: 'grey',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        backgroundColor: '#b71c1c',
                        color: 'white',
                        px: 2,
                        py: 1
                      }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            fontSize: '0.875rem'
                          }}
                        >
                          📝 REVIEW TITLE
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2, backgroundColor: 'white' }}>
                        <Typography variant="h6" fontWeight={600} color="grey">
                          {selectedReview.title}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Review Comment Section - Inside Border */}
                    <Box sx={{
                      mb: 3,
                      border: '2px solid',
                      borderColor: 'grey',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        backgroundColor: '#b71c1c',
                        color: 'white',
                        px: 2,
                        py: 1
                      }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            fontSize: '0.875rem'
                          }}
                        >
                          💬 CUSTOMER COMMENT
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2, backgroundColor: 'white' }}>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                          {selectedReview.comment}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Media Gallery */}
                    {selectedReview.media && selectedReview.media.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: 'wrap', gap: 1 }}>
                          <Typography
                            variant="overline"
                            sx={{
                              fontWeight: 700,
                              color: 'text.secondary',
                              letterSpacing: '0.1em',
                              fontSize: '0.85rem'
                            }}
                          >
                            🖼️ UPLOADED MEDIA ({selectedReview.media.length})
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          {selectedReview.media.map((media, idx) => (
                            <Grid item xs={6} sm={4} key={idx}>
                              <Paper
                                onClick={() => handleOpenMedia(selectedReview, idx)}
                                sx={{
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  border: "2px solid",
                                  borderColor: "divider",
                                  position: "relative",
                                  "&:hover": {
                                    borderColor: "primary.main",
                                    transform: 'scale(1.02)',
                                    transition: 'all 0.2s'
                                  }
                                }}
                              >
                                {media.type === "video" ? (
                                  <Box sx={{
                                    aspectRatio: "1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#333"
                                  }}>
                                    <PlayArrowIcon sx={{ color: "white", fontSize: "3rem" }} />
                                  </Box>
                                ) : (
                                  <CardMedia
                                    component="img"
                                    image={media.thumbnail || media.url}
                                    alt={`Media ${idx + 1}`}
                                    sx={{ aspectRatio: "1", objectFit: "cover" }}
                                  />
                                )}
                                <Box sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  backgroundColor: "rgba(0,0,0,0.75)",
                                  color: "white",
                                  p: 0.75,
                                  display: "flex",
                                  justifyContent: "space-between"
                                }}>
                                  <Typography variant="caption" fontSize="0.7rem">{media.mediaType || media.type}</Typography>
                                  <Typography variant="caption" fontSize="0.7rem">{media.fileSize}</Typography>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    {/* Customer Info */}
                    <Box sx={{ pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          color: 'text.secondary',
                          letterSpacing: '0.1em',
                          fontSize: '0.75rem',
                          display: 'block',
                          mb: 2
                        }}
                      >
                        👤 CUSTOMER INFORMATION
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ position: "relative" }}>
                              {selectedReview.userAvatar ? (
                                <Avatar
                                  src={selectedReview.userAvatar}
                                  alt={selectedReview.userName}
                                  sx={{ width: 56, height: 56 }}
                                />
                              ) : (
                                <Skeleton
                                  variant="circular"
                                  width={56}
                                  height={56}
                                  sx={{ bgcolor: '#e0e0e0' }}
                                />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Customer
                              </Typography>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {selectedReview.userName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {selectedReview.userCountry}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Date Posted
                          </Typography>
                          <Typography variant="subtitle1" fontWeight={500}>
                            📅 {formatDate(selectedReview.date)}
                          </Typography>
                          {selectedReview.apiData?.updatedAt && (
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                              Last updated: {formatDate(formatDateFromArray(selectedReview.apiData.updatedAt))}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>

                  {/* Footer */}
                  <Box sx={{
                    p: 2.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "#f9f9f9"
                  }}>
                    <Button
                      variant="contained"
                      onClick={handleCloseDetails}
                      fullWidth
                      sx={{
                        backgroundColor: "#b71c1c",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#b71c1c"
                        }
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Modal>

        {/* Media Viewer Modal with Carousel */}
        <Modal
          open={!!selectedMedia}
          onClose={handleCloseMedia}
          aria-labelledby="media-viewer-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 1000,
            maxHeight: '90vh',
            bgcolor: 'rgba(0,0,0,0.95)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
            {selectedMedia && selectedReview && (
              <>
                {/* Close Button */}
                <IconButton
                  onClick={handleCloseMedia}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "#f5f5f5"
                    },
                    zIndex: 10
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* Main Media Display */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '60vh',
                  maxHeight: '70vh',
                  position: 'relative',
                  p: 2
                }}>
                  {/* Previous Button */}
                  {selectedReview.media && selectedReview.media.length > 1 && (
                    <IconButton
                      onClick={handlePreviousMedia}
                      sx={{
                        position: "absolute",
                        left: 16,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        "&:hover": {
                          backgroundColor: "white"
                        },
                        zIndex: 2
                      }}
                    >
                      <ArrowBackIosNewIcon />
                    </IconButton>
                  )}

                  {/* Media Content */}
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selectedMedia.type === "video" ? (
                      <video
                        controls
                        autoPlay
                        src={selectedMedia.url}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <img
                        src={selectedMedia.url}
                        alt="Full size media"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    )}
                  </Box>

                  {/* Next Button */}
                  {selectedReview.media && selectedReview.media.length > 1 && (
                    <IconButton
                      onClick={handleNextMedia}
                      sx={{
                        position: "absolute",
                        right: 16,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        "&:hover": {
                          backgroundColor: "white"
                        },
                        zIndex: 2
                      }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  )}
                </Box>

                {/* Media Info Bar */}
                <Box sx={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  color: 'white',
                  p: 2.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Typography variant="body2">
                      {selectedMedia.fileName || `Media ${(currentMediaIndex || 0) + 1}`}
                    </Typography>
                    <Typography variant="body2">
                      {selectedMedia.fileSize || ""}
                    </Typography>
                    <Typography variant="body2">
                      {selectedMedia.mediaType || selectedMedia.type}
                    </Typography>
                  </Box>
                  {selectedReview.media && selectedReview.media.length > 1 && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(currentMediaIndex || 0) + 1} / {selectedReview.media.length}
                    </Typography>
                  )}
                </Box>

                {/* Thumbnail Navigation */}
                {selectedReview.media && selectedReview.media.length > 1 && (
                  <Box sx={{
                    backgroundColor: 'rgba(0,0,0,0.9)',
                    p: 2,
                    overflowX: 'auto',
                    display: 'flex',
                    gap: 1.5,
                    justifyContent: 'center'
                  }}>
                    {selectedReview.media.map((media, idx) => (
                      <Box
                        key={idx}
                        onClick={() => {
                          setSelectedMedia(media);
                          setCurrentMediaIndex(idx);
                        }}
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          cursor: 'pointer',
                          border: '3px solid',
                          borderColor: (currentMediaIndex || 0) === idx ? 'primary.main' : 'transparent',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          opacity: (currentMediaIndex || 0) === idx ? 1 : 0.6,
                          transition: 'all 0.2s',
                          '&:hover': {
                            opacity: 1,
                            borderColor: 'primary.light'
                          }
                        }}
                      >
                        {media.type === "video" ? (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#333'
                          }}>
                            <PlayArrowIcon sx={{ color: 'white', fontSize: '2rem' }} />
                          </Box>
                        ) : (
                          <img
                            src={media.thumbnail || media.url}
                            alt={`Thumbnail ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Modal>
      </>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <FooterManual />
    </Box>
  );
};

export default UserReviews;